import React, { useEffect, useState, useRef } from "react";
import { X, Play, ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import api from "../../lib/api";

const COLORS = [
  { key: "red", hex: "#FF3B30", label: "Rojo" },
  { key: "green", hex: "#32D74B", label: "Verde" },
  { key: "yellow", hex: "#FFD60A", label: "Amarillo" },
  { key: "blue", hex: "#007AFF", label: "Azul" },
];

/**
 * Simon de Memoria: la app muestra una secuencia que va creciendo de a 1
 * color por ronda. El atleta debe reproducirla tocando los botones en
 * el mismo orden. Si falla, fin del juego — guarda el "nivel máximo".
 */
export default function SimonMemoryPlayer({ onClose }) {
  const [phase, setPhase] = useState("intro"); // intro | watch | input | done
  const [sequence, setSequence] = useState([]);
  const [userIdx, setUserIdx] = useState(0);
  const [activeColor, setActiveColor] = useState(null);
  const [level, setLevel] = useState(0); // ronda actual = longitud de la secuencia
  const startedAtRef = useRef(null);

  const nextRound = (prev) => {
    const newColor = COLORS[Math.floor(Math.random() * COLORS.length)].key;
    const seq = [...prev, newColor];
    setSequence(seq);
    setUserIdx(0);
    setLevel(seq.length);
    setPhase("watch");
  };

  const start = () => {
    startedAtRef.current = new Date();
    nextRound([]);
  };

  // Reproducir secuencia
  useEffect(() => {
    if (phase !== "watch") return;
    let cancelled = false;
    const play = async () => {
      await new Promise((r) => setTimeout(r, 500));
      for (let i = 0; i < sequence.length; i++) {
        if (cancelled) return;
        setActiveColor(sequence[i]);
        await new Promise((r) => setTimeout(r, 550));
        setActiveColor(null);
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!cancelled) setPhase("input");
    };
    play();
    return () => { cancelled = true; };
  }, [phase, sequence]);

  const handleTap = (key) => {
    if (phase !== "input") return;
    setActiveColor(key);
    setTimeout(() => setActiveColor(null), 200);
    const expected = sequence[userIdx];
    if (key !== expected) {
      // Falló — fin del juego
      setPhase("done");
      try {
        api.post("/neuroscience/results", {
          config_id: "default_simon",
          started_at: startedAtRef.current.toISOString(),
          finished_at: new Date().toISOString(),
          total_taps: userIdx + 1,
          correct_taps: userIdx,
          notes: `Simon · Nivel máximo: ${sequence.length - 1}`,
        }).catch(() => {});
      } catch { /* noop */ }
      return;
    }
    const nextIdx = userIdx + 1;
    if (nextIdx >= sequence.length) {
      // Completó la ronda → siguiente
      setTimeout(() => nextRound(sequence), 500);
    } else {
      setUserIdx(nextIdx);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-app flex flex-col items-center justify-center p-6" data-testid="simon-player">
      <button onClick={onClose} className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-black/60 text-white" data-testid="simon-close">
        <X size={18} weight="bold" />
      </button>

      {phase === "intro" && (
        <div className="text-center max-w-md text-white">
          <div className="font-display font-black uppercase text-3xl tracking-tighter mb-3">Simon de Memoria</div>
          <p className="text-muted text-sm mb-6 leading-relaxed">
            Te muestro una secuencia de colores que crece de a 1 por ronda. Repetila tocando los botones en el mismo orden. Si fallás, terminó.
          </p>
          <button onClick={start} className="btn-primary w-full !py-4 flex items-center justify-center gap-2" data-testid="simon-start">
            <Play size={20} weight="fill" /> Empezar
          </button>
        </div>
      )}

      {phase !== "intro" && phase !== "done" && (
        <>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-widest pointer-events-none">
            Ronda <span className="text-primary-app font-bold">{level}</span> · {phase === "watch" ? "Observá" : `Repetí (${userIdx + 1}/${sequence.length})`}
          </div>
          <div className="grid grid-cols-2 gap-3 w-full max-w-sm aspect-square" data-testid="simon-pad">
            {COLORS.map((c) => (
              <button
                key={c.key}
                onClick={() => handleTap(c.key)}
                disabled={phase !== "input"}
                className="border-4 border-[#0A0A0A] transition-all duration-100 disabled:opacity-100"
                style={{
                  backgroundColor: c.hex,
                  opacity: activeColor === c.key ? 1 : 0.4,
                  transform: activeColor === c.key ? "scale(0.97)" : "scale(1)",
                }}
                data-testid={`simon-btn-${c.key}`}
                aria-label={c.label}
              />
            ))}
          </div>
        </>
      )}

      {phase === "done" && (
        <div className="text-center max-w-md text-white">
          <CheckCircle size={48} weight="duotone" className="text-[#32D74B] mx-auto mb-4" />
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Nivel máximo</div>
          <div className="font-display font-black text-6xl text-primary-app mb-6">{sequence.length - 1}</div>
          <div className="flex gap-3">
            <button onClick={() => { setPhase("intro"); setSequence([]); }} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="simon-replay">
              <ArrowCounterClockwise size={16} weight="bold" /> Otra vez
            </button>
            <button onClick={onClose} className="btn-primary flex-1" data-testid="simon-finish">Terminar</button>
          </div>
        </div>
      )}
    </div>
  );
}
